import { create } from 'zustand';
import {
  LaborProductivityDashboardStore,
  ActiveMemberByPosition,
  CalloutShiftByType,
  ReleasedShiftByEmployee,
  CalloutShiftsByEmploymentStatusResponse,
  ActiveByPositionResponse,
  ActiveByPositionResponseItem,
} from 'pages/ReportsPage/components/AnalyticsDashboards/LaborProductivityDashboard/store/laborProductivityDashboard.store.types';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import { createDashboardsRequest } from 'pages/ReportsPage/components/AnalyticsDashboards/createDashboardsRequest';
import { fetchDashboardPage } from 'pages/ReportsPage/components/AnalyticsDashboards/fetchDashboardPage';
import { hasDataLockRejection } from 'pages/ReportsPage/store/reportsDataHealth.store';
import { LABOR_PRODUCTIVITY_DATA_LOCKED_STUB } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborProductivityDashboard/store/laborProductivityDashboard.stub';
import {
  DASHBOARDS_TEAM_MEMBERS_CALLOUT_SHIFTS_BY_EMPLOYMENT_STATUS,
  DASHBOARDS_TEAM_MEMBERS_ACTIVE_BY_POSITION,
} from 'constants/endpoints';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';

const INITIAL_STATE = {
  activeMembersByPosition: null as ActiveMemberByPosition[] | null,
  activeByPositionPage: 1,
  activeByPositionTotalPages: 1,
  activeByPositionLoadingMore: false,
  calloutShiftsByType: null as CalloutShiftByType[] | null,
  releasedShiftsByEmployee: null as ReleasedShiftByEmployee[] | null,
  isLoading: false,
  error: null as string | null,
};

function mapActiveMembersByPosition(
  rows: ActiveByPositionResponseItem[],
): ActiveMemberByPosition[] {
  return rows.map((item) => ({
    position: item.position_name,
    count: item.team_member_count,
  }));
}

const DEFAULT_CALLOUT_SHIFT_TYPES = ['casual', 'flex', 'full_time', 'part_time'];

function mapCalloutShiftsByType(
  response: CalloutShiftsByEmploymentStatusResponse,
): CalloutShiftByType[] {
  const rows = response?.callout_shifts_by_employment_status ?? [];
  if (rows.length === 0) {
    return DEFAULT_CALLOUT_SHIFT_TYPES.map((type) => ({ type, count: 0, percentage: 0 }));
  }
  return rows.map((item) => ({
    type: item.employment_status,
    count: item.shift_count,
    percentage: item.shift_prct,
  }));
}

function buildFilterQuery(
  dateRange: [string, string],
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  const filters: Record<string, unknown> = {
    time_range: {
      start_time: dateRange[0],
      end_time: dateRange[1],
    },
  };

  if (locationIds && locationIds.length > 0) {
    filters.community_ids = locationIds.map(Number);
  }
  if (positions && positions.length > 0) {
    filters.position_ids = positions.map(Number);
  }
  if (employmentTypes.length > 0) {
    filters.employment_statuses = employmentTypes;
  }

  return { filters };
}

let fetchId = 0;
let activeByPositionBaseQuery: Record<string, unknown> | null = null;

export const laborProductivityDashboardStore = create<LaborProductivityDashboardStore>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,

    fetchData: (dateRange, locationIds, positions, employmentTypes) => {
      if (isSelectionCleared(locationIds, positions)) {
        fetchId++;
        set({ ...INITIAL_STATE });
        return () => {
          fetchId++;
        };
      }
      const currentFetchId = ++fetchId;
      set({ isLoading: true, error: null });

      const query = buildFilterQuery(dateRange, locationIds, positions, employmentTypes);
      activeByPositionBaseQuery = query;

      const calloutShiftsRequest = createDashboardsRequest<CalloutShiftsByEmploymentStatusResponse>(
        {
          url: DASHBOARDS_TEAM_MEMBERS_CALLOUT_SHIFTS_BY_EMPLOYMENT_STATUS(),
          method: 'GET',
          query,
        },
      );

      const activeByPositionRequest = fetchDashboardPage<
        ActiveByPositionResponse,
        ActiveByPositionResponseItem
      >({
        url: DASHBOARDS_TEAM_MEMBERS_ACTIVE_BY_POSITION(),
        query,
        page: 1,
        selectItems: (body) => body.active_by_position ?? [],
      });

      Promise.allSettled([calloutShiftsRequest, activeByPositionRequest]).then((results) => {
        const isDataLocked = hasDataLockRejection(results);
        if (currentFetchId !== fetchId) return;
        if (isDataLocked) {
          set({ ...INITIAL_STATE, ...LABOR_PRODUCTIVITY_DATA_LOCKED_STUB, isLoading: false });
          return;
        }
        const [calloutShiftsResult, activeByPositionResult] = results;

        const activeByPositionData =
          activeByPositionResult.status === 'fulfilled' ? activeByPositionResult.value : null;

        set((state) => ({
          ...state,
          calloutShiftsByType:
            calloutShiftsResult.status === 'fulfilled'
              ? mapCalloutShiftsByType(calloutShiftsResult.value.data)
              : null,
          activeMembersByPosition: activeByPositionData
            ? mapActiveMembersByPosition(activeByPositionData.items)
            : null,
          activeByPositionPage: activeByPositionData?.meta?.current_page ?? 1,
          activeByPositionTotalPages: activeByPositionData?.meta?.total_pages ?? 1,
          activeByPositionLoadingMore: false,
          isLoading: false,
          error: null,
        }));
      });

      return () => {
        fetchId++;
      };
    },
    loadMoreActiveByPosition: () => {
      const { activeByPositionLoadingMore, activeByPositionPage, activeByPositionTotalPages } =
        get();
      if (
        activeByPositionLoadingMore ||
        activeByPositionPage >= activeByPositionTotalPages ||
        !activeByPositionBaseQuery
      ) {
        return;
      }
      const currentFetchId = fetchId;
      const nextPage = activeByPositionPage + 1;
      set({ activeByPositionLoadingMore: true });

      fetchDashboardPage<ActiveByPositionResponse, ActiveByPositionResponseItem>({
        url: DASHBOARDS_TEAM_MEMBERS_ACTIVE_BY_POSITION(),
        query: activeByPositionBaseQuery,
        page: nextPage,
        selectItems: (body) => body.active_by_position ?? [],
      })
        .then(({ items, meta }) => {
          if (currentFetchId !== fetchId) return;
          set((state) => ({
            activeMembersByPosition: [
              ...(state.activeMembersByPosition ?? []),
              ...mapActiveMembersByPosition(items),
            ],
            activeByPositionPage: meta?.current_page ?? nextPage,
            activeByPositionTotalPages: meta?.total_pages ?? state.activeByPositionTotalPages,
            activeByPositionLoadingMore: false,
          }));
        })
        .catch(() => {
          if (currentFetchId !== fetchId) return;
          set({ activeByPositionLoadingMore: false });
        });
    },
    setData: (data) => set((state) => ({ ...state, ...data })),
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    reset: () => {
      fetchId = 0;
      activeByPositionBaseQuery = null;
      set(INITIAL_STATE);
    },
  })),
);

export const useLaborProductivityDashboardStore = laborProductivityDashboardStore;
